/** Help content keyed by route path. Each entry has title, intro, steps[], tips[]. */

const HELP = {
  "/dashboard": {
    title: "Tableau de bord",
    intro: "Vue d'ensemble du club mise à jour en temps réel : effectif, finances, alertes et événements importants.",
    steps: [
      "Consultez les <b>6 indicateurs clés</b> en haut (joueuses, solde, transferts, personnel, membres, documents).",
      "Le <b>graphique Entrées/Sorties</b> affiche les 6 derniers mois.",
      "Les <b>alertes administratives</b> listent les actions à mener (transferts en attente, cotisations impayées, blessures).",
    ],
    tips: ["Consultez ce tableau chaque lundi matin pour planifier la semaine."],
  },
  "/equipes": {
    title: "Gestion des équipes",
    intro: "Effectif complet par catégorie : Senior Pro Dames, Amateur, U17, U13.",
    steps: [
      "Sélectionnez la catégorie via les onglets en haut.",
      "Cliquez sur <b>Nouvelle joueuse</b> pour ajouter une fiche.",
      "Cliquez sur <b>Détails</b> (ou la photo) pour ouvrir la fiche complète (Infos, Stats, Blessures, Observations).",
      "Téléversez une photo de la joueuse (JPG/PNG, max 5 Mo) directement depuis le formulaire.",
    ],
    tips: ["Ajoutez une observation après chaque match important : c'est votre suivi de progression sur la saison."],
  },
  "/calendrier": {
    title: "Calendrier",
    intro: "Matchs, entraînements et événements du club.",
    steps: [
      "Cliquez sur <b>Nouvel événement</b> et choisissez le type.",
      "Pour un match : précisez la catégorie, l'adversaire et l'heure.",
      "Après le match, cliquez sur <b>Score</b> pour saisir le résultat.",
      "Cliquez sur <b>Stats</b> pour ouvrir la feuille de match (saisie individuelle des stats par joueuse).",
    ],
    tips: ["La feuille de match fonctionne hors-ligne ! Les données sont synchronisées automatiquement à la reconnexion."],
  },
  "/classements": {
    title: "Classements & leaders",
    intro: "7 classements basés sur les feuilles de match : top scoreuses, passeuses, rebondeuses, défense, interceptions, minutes, indice +/-.",
    steps: [
      "Choisissez la <b>période</b> (Mois en cours ou Saison entière).",
      "Filtrez par <b>catégorie d'équipe</b> si besoin.",
      "La performeuse n°1 du classement points est mise en avant dans le bandeau vert.",
    ],
    tips: ["L'indice +/- est calculé ainsi : pts + pas + reb + cnt + int – fautes."],
  },
  "/communication": {
    title: "Communication & annonces",
    intro: "Diffusez des annonces internes ciblées par catégorie d'équipe ou par rôle.",
    steps: [
      "Cliquez sur <b>Nouvelle annonce</b>.",
      "Choisissez la priorité (Info, Important, Urgent).",
      "Cochez les catégories d'équipe et/ou les rôles destinataires.",
      "Épinglez les annonces les plus importantes en haut.",
    ],
    tips: ["Aucun ciblage = visible par tous. Les destinataires voient un badge rouge dans la sidebar."],
  },
  "/transferts": {
    title: "Transferts",
    intro: "Mouvements de joueuses : entrants, sortants, prêts, renouvellements, fins de contrat.",
    steps: [
      "Cliquez sur <b>Nouveau transfert</b>.",
      "Le dossier est créé en statut <b>En attente</b>.",
      "Validez ou refusez via le menu déroulant après décision du bureau.",
    ],
    tips: ["Les transferts en attente apparaissent automatiquement dans les alertes du tableau de bord."],
  },
  "/finances": {
    title: "Finances",
    intro: "Suivi des entrées et sorties d'argent du club.",
    steps: [
      "Cliquez sur <b>Nouvelle opération</b>.",
      "Choisissez Entrée ou Sortie, la catégorie et le montant en FCFA.",
      "Toutes les opérations sont triées par date et exportables en CSV.",
    ],
    tips: ["Pour un rapport officiel (CA, sponsors), utilisez le Bilan comptable annuel dans Budget prévisionnel."],
  },
  "/budget": {
    title: "Budget prévisionnel",
    intro: "Comparez vos prévisions annuelles avec les opérations réellement enregistrées.",
    steps: [
      "Sélectionnez l'année.",
      "Ajoutez des lignes prévisionnelles via <b>Nouvelle ligne</b>.",
      "La page calcule automatiquement le <b>% d'atteinte</b> (vert = bon, orange = attention, rouge = problème).",
      "Exportez le <b>Bilan synthétique</b> ou <b>détaillé</b> en PDF officiel.",
    ],
    tips: ["Pour les sorties : ≤100% = sous budget (vert), >120% = sur-dépense (rouge)."],
  },
  "/rh": {
    title: "Personnel & RH",
    intro: "Gérez salariés, bénévoles, entraîneurs, dirigeants et staff médical.",
    steps: [
      "Cliquez sur <b>Nouveau membre</b>.",
      "Choisissez la catégorie (Salarié, Bénévole, Entraîneur…) et la fonction.",
      "Renseignez le contrat, le salaire et téléversez une photo.",
    ],
    tips: ["Les fiches RH alimentent la génération de contrats de travail, fiches de paie et ordres de mission."],
  },
  "/membres": {
    title: "Membres & cartes",
    intro: "Adhérents, joueuses, dirigeants, bénévoles, supporters.",
    steps: [
      "Cliquez sur <b>Nouvelle inscription</b>.",
      "À la réception du paiement, cochez le bouton <b>Cotisation payée</b>.",
      "Cliquez sur l'icône 📥 pour générer la <b>carte de membre PDF</b> (avec photo).",
    ],
    tips: ["Exportez la liste complète des membres en CSV pour vos déclarations annuelles."],
  },
  "/documents": {
    title: "Génération de documents",
    intro: "12 modèles officiels personnalisés AMAL VBC (logo, en-tête vert, signatures).",
    steps: [
      "Cliquez sur la carte du document souhaité.",
      "Remplissez le formulaire correspondant.",
      "Cliquez sur <b>Générer le PDF</b> : le document s'ouvre, se télécharge et est archivé.",
    ],
    tips: ["Chaque document reçoit un numéro unique de type AMAL-2026-00042 dans l'historique."],
  },
  "/utilisateurs": {
    title: "Utilisateurs & accès",
    intro: "Gérez les comptes utilisateurs et leurs codes d'accès personnels.",
    steps: [
      "Cliquez sur <b>Nouvel utilisateur</b>, choisissez le rôle.",
      "Pour un rôle joueur/parent, sélectionnez la joueuse liée.",
      "Le code d'accès est affiché <b>une seule fois</b> — copiez-le immédiatement.",
      "Bouton <b>Sauvegarde</b> : télécharge un ZIP de toutes les données du club.",
      "Bouton <b>Manuel PDF</b> : télécharge le manuel d'utilisation imprimable (22 pages).",
    ],
    tips: ["⚠️ Le bouton rouge Réinitialiser supprime TOUTES les données du club. À utiliser uniquement en début de saison après sauvegarde."],
  },
  "/portail": {
    title: "Mon portail joueuse",
    intro: "Votre espace personnel : profil, calendrier, matchs joués et annonces du club.",
    steps: [
      "<b>Mon profil</b> : vos informations, votre suivi médical et les observations de l'entraîneur.",
      "<b>Annonces</b> : communications du club (badge rouge si non lu).",
      "<b>Calendrier équipe</b> : vos prochains matchs et entraînements.",
      "<b>Mes matchs</b> : historique avec vos statistiques personnelles.",
    ],
    tips: ["Vous pouvez installer l'app sur votre téléphone (PWA) pour y accéder hors connexion."],
  },
};

export function getHelp(pathname) {
  // Find best match by checking exact then prefix
  if (HELP[pathname]) return HELP[pathname];
  for (const key of Object.keys(HELP)) {
    if (pathname.startsWith(key)) return HELP[key];
  }
  return null;
}
